import { MapPin, Camera, Brain, Globe, Search, AlertCircle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ScanFeedEvent } from "@/hooks/useScanEvents";

interface EventFeedItemProps {
  feedEvent: ScanFeedEvent;
  onLocationClick?: (lat: number, lng: number) => void;
}

export function EventFeedItem({ feedEvent, onLocationClick }: EventFeedItemProps) {
  const { event } = feedEvent;

  switch (event.type) {
    case "keyword_location_found":
      return (
        <button
          type="button"
          className="animate-feed-item-in flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-muted/50 transition-colors text-xs"
          onClick={() => onLocationClick?.(event.lat, event.lng)}
        >
          <Search className="size-3.5 text-amber-500 shrink-0" />
          <span className="truncate font-medium">{event.name}</span>
          {event.primaryType && (
            <Badge variant="secondary" className="text-[10px] px-1 py-0 shrink-0">
              {event.primaryType.replace(/_/g, " ")}
            </Badge>
          )}
        </button>
      );

    case "location_found":
      return (
        <button
          type="button"
          className="animate-feed-item-in flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-muted/50 transition-colors text-xs"
          onClick={() => onLocationClick?.(event.lat, event.lng)}
        >
          <MapPin className="size-3.5 text-blue-500 shrink-0" />
          <span className="truncate font-medium">{event.name}</span>
          {event.primaryType && (
            <Badge variant="secondary" className="text-[10px] px-1 py-0 shrink-0">
              {event.primaryType.replace(/_/g, " ")}
            </Badge>
          )}
        </button>
      );

    case "screenshot_complete":
      return (
        <div className="animate-feed-item-in flex items-center gap-2 px-2 py-1.5 text-xs">
          <Camera className={`size-3.5 shrink-0 ${event.success ? "text-green-500" : "text-red-500"}`} />
          <span className="truncate text-muted-foreground">
            Screenshot {event.success ? "captured" : "failed"}
          </span>
          {event.screenshotUrl && (
            <img
              src={event.screenshotUrl}
              alt=""
              className="size-8 rounded object-cover shrink-0 ml-auto"
            />
          )}
        </div>
      );

    case "classification_complete":
      return (
        <div className="animate-feed-item-in flex items-center gap-2 px-2 py-1.5 text-xs">
          <Brain className={`size-3.5 shrink-0 ${event.hasSportField ? "text-green-500" : "text-orange-500"}`} />
          <span className="truncate font-medium">{event.name}</span>
          {event.hasSportField ? (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 text-[10px] px-1 py-0 shrink-0">
              Sport Field
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px] px-1 py-0 shrink-0">
              No Field
            </Badge>
          )}
          {event.confidence !== null && (
            <span className="text-muted-foreground shrink-0 ml-auto">
              {Math.round(event.confidence * 100)}%
            </span>
          )}
        </div>
      );

    case "website_classify_complete":
      return (
        <div className="animate-feed-item-in flex items-center gap-2 px-2 py-1.5 text-xs">
          <Globe className={`size-3.5 shrink-0 ${event.success ? "text-purple-500" : "text-red-500"}`} />
          <span className="truncate font-medium">{event.name}</span>
          {event.ownership && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0 capitalize">
              {event.ownership}
            </Badge>
          )}
        </div>
      );

    case "web_research_complete":
      return (
        <div className="animate-feed-item-in flex items-center gap-2 px-2 py-1.5 text-xs">
          <Search className={`size-3.5 shrink-0 ${event.success ? "text-cyan-500" : "text-red-500"}`} />
          <span className="truncate font-medium">{event.name}</span>
          {event.ownership && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0 capitalize">
              {event.ownership}
            </Badge>
          )}
          {event.confidence !== null && (
            <span className="text-muted-foreground shrink-0 ml-auto">
              {Math.round(event.confidence * 100)}%
            </span>
          )}
        </div>
      );

    case "scan_complete":
      return (
        <div className="animate-feed-item-in flex items-center gap-2 px-2 py-1.5 text-xs text-green-600 dark:text-green-400">
          <CheckCircle2 className="size-3.5 shrink-0" />
          <span className="font-medium">
            Scan complete — {event.totalLocations} locations, {event.sportFieldsFound} sport fields
          </span>
        </div>
      );

    case "scan_error":
      return (
        <div className="animate-feed-item-in flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 dark:text-red-400">
          <AlertCircle className="size-3.5 shrink-0" />
          <span className="truncate">{event.error}</span>
        </div>
      );

    default:
      return null;
  }
}
